
import React, { useState } from 'react';
import styled from '@emotion/styled';
import AdvancedCanvasEditor from './canvas/AdvancedCanvasEditor';
import LidoImageUploader from './canvas/LidoImageUploader';
import LidoProductPreview from './canvas/LidoProductPreview';
import { useUserImages } from '@/hooks/use-user-images';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ImageIcon, Eye } from 'lucide-react';

// TypeScript interfaces
interface Editor {
  addImageLayer: (data: any) => Promise<void>;
  addTextLayer: (data: any) => Promise<void>;
  addShapeLayer: (data: any) => Promise<void>;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
  export: (options: any) => Promise<string>;
  toJSON: () => Promise<any>;
  loadFromJSON: (data: any) => Promise<void>;
  destroy: () => void;
}

interface UploadedImage {
  publicUrl: string;
  width?: number;
  height?: number;
}

interface CanvasElement {
  id: string;
  type: 'image' | 'text' | 'shape';
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  zIndex: number;
}

interface LidoAdvancedCanvasEditorProps {
  storeId: string;
}

// Styled components
const EditorLayout = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr 320px;
  gap: 1rem;
  height: 100vh;
  
  @media (max-width: 1200px) {
    grid-template-columns: 250px 1fr 280px;
  }
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr auto;
    height: auto;
  }
`;

const LeftPanel = styled.div`
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  
  @media (max-width: 1024px) {
    order: 3;
  }
`;

const CanvasArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  
  @media (max-width: 1024px) {
    order: 2;
  }
`;

const RightPanel = styled.div`
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  
  @media (max-width: 1024px) {
    order: 1;
  }
`;

const PanelHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f9fafb;
  font-weight: 600;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PanelContent = styled.div`
  padding: 1rem;
  overflow-y: auto;
  max-height: calc(100vh - 120px);
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
`;

const ImageItem = styled.div`
  aspect-ratio: 1;
  background: #f3f4f6;
  border-radius: 8px;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.2s ease-in-out;
  border: 2px solid transparent;
  
  &:hover {
    transform: scale(1.05);
    border-color: #3B82F6;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const SectionTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const LidoAdvancedCanvasEditor: React.FC<LidoAdvancedCanvasEditorProps> = ({ storeId }) => {
  const [editor, setEditor] = useState<Editor | null>(null);
  const [currentDesign, setCurrentDesign] = useState<string>('');
  const [designElements, setDesignElements] = useState<CanvasElement[]>([]);
  const { images, uploadImage } = useUserImages(storeId);

  const handleEditorReady = (editorInstance: Editor) => {
    setEditor(editorInstance);
  };

  const handleImageUpload = async (file: File): Promise<UploadedImage> => {
    const result = await uploadImage(file, 'canvas-assets', []);
    return {
      publicUrl: result.storage_path,
      width: result.width || undefined,
      height: result.height || undefined
    };
  };

  const handleImageClick = async (imagePath: string) => {
    if (!editor) return;
    
    await editor.addImageLayer({
      image: {
        url: imagePath,
        naturalWidth: 200,
        naturalHeight: 200
      },
      position: { x: 100, y: 100 },
      size: { width: 200, height: 200 }
    });
  };

  const handleDesignChange = (designUrl: string, elements: CanvasElement[]) => {
    setCurrentDesign(designUrl);
    setDesignElements(elements);
  };

  return (
    <EditorLayout>
      {/* Left Panel - Assets and Tools */}
      <LeftPanel>
        <PanelHeader>
          <ImageIcon size={16} />
          Recursos
        </PanelHeader>
        
        <PanelContent>
          <Tabs defaultValue="uploads" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="uploads">Uploads</TabsTrigger>
              <TabsTrigger value="clipart">Clipart</TabsTrigger>
            </TabsList>
            
            <TabsContent value="uploads" className="space-y-4">
              <div>
                <SectionTitle>Upload de Imagens</SectionTitle>
                <LidoImageUploader
                  editor={editor}
                  onImageUpload={handleImageUpload}
                />
              </div>
              
              <div>
                <SectionTitle>Minhas Imagens</SectionTitle>
                <ImageGrid>
                  {images.slice(0, 12).map((image) => (
                    <ImageItem
                      key={image.id}
                      onClick={() => handleImageClick(image.storage_path)}
                      title={image.original_name}
                    >
                      <img
                        src={image.storage_path}
                        alt={image.original_name}
                      />
                    </ImageItem>
                  ))}
                </ImageGrid>
              </div>
            </TabsContent>
            
            <TabsContent value="clipart">
              <SectionTitle>Formas disponíveis no editor</SectionTitle>
              <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                Use as ferramentas do editor para adicionar formas geométricas
              </p>
            </TabsContent>
          </Tabs>
        </PanelContent>
      </LeftPanel>

      {/* Main Canvas Area */}
      <CanvasArea>
        <Card className="flex-1">
          <CardContent className="p-4">
            <AdvancedCanvasEditor
              onEditorReady={handleEditorReady}
              onDesignChange={handleDesignChange}
              width={800}
              height={600}
            />
          </CardContent>
        </Card>
      </CanvasArea>

      {/* Right Panel - Product Preview */}
      <RightPanel>
        <PanelHeader>
          <Eye size={16} />
          Preview do Produto
        </PanelHeader>
        
        <PanelContent>
          <LidoProductPreview 
            designImageUrl={currentDesign}
            designElements={designElements}
            storeId={storeId}
          />
        </PanelContent>
      </RightPanel>
    </EditorLayout>
  );
};

export default LidoAdvancedCanvasEditor;
